﻿import { useState, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { z } from 'zod';
import {
  ChevronLeft, ChevronRight, Plus, MapPin, Trash2, Pencil, Calendar, Clock, BookOpen, Users, Copy, Check,
  AlertCircle, CircleDot, Upload, ArrowLeftRight, Lock, ChevronDown,
} from 'lucide-react';
import {
  format, startOfMonth, endOfMonth, eachDayOfInterval,
  startOfWeek, endOfWeek, isSameMonth, isSameDay, parseISO, startOfToday,
} from 'date-fns';
import { PERMISSIONS, createDmActivitySchema, updateDmActivitySchema } from '@pms/shared';
import type { CreateDmActivityInput, UpdateDmActivityInput } from '@pms/shared';
import { dmTeamService } from '@/services/dm-team.service';
import type { DmActivity, DmActivityStatus, DmUserRef } from '@/services/dm-team.service';
import { usePermission } from '@/hooks/usePermission';
import { DmPageMultiSelect } from '@/components/dm-team/DmPageMultiSelect';
import { isActivityLocked, activityLockHint, CANCEL_CONFIRM_TITLE, CANCEL_CONFIRM_TEXT } from './activityLock';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// ── Status helpers ────────────────────────────────────────────────────────────

function activityChipClass(status: string): string {
  switch (status) {
    case 'pending':     return 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200 dark:bg-muted dark:text-slate-400 dark:border-border dark:hover:bg-muted';
    case 'overdue':     return 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-900/50 dark:hover:bg-rose-900/40';
    case 'completed':   return 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-900/50 dark:hover:bg-yellow-900/40';
    case 'cancelled':   return 'bg-zinc-100 text-zinc-500 border-zinc-300 line-through decoration-rose-400/70 hover:bg-zinc-200 dark:bg-zinc-800/60 dark:text-zinc-400 dark:border-zinc-700 dark:hover:bg-zinc-800';
    case 'uploaded':    return 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-900/50 dark:hover:bg-emerald-900/40';
    default:            return 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-muted dark:text-slate-400 dark:border-border';
  }
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'pending':     return 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-muted dark:text-slate-400 dark:border-border';
    case 'overdue':     return 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-900/50';
    case 'completed':   return 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-900/50';
    case 'cancelled':   return 'bg-zinc-100 text-rose-700 border-rose-200 dark:bg-zinc-800/60 dark:text-rose-400 dark:border-rose-900/50';
    case 'uploaded':    return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-900/50';
    default:            return 'bg-slate-100 text-slate-600 dark:bg-muted dark:text-slate-400';
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case 'pending':     return 'New Entry';
    case 'overdue':     return 'Not Completed';
    case 'completed':   return 'Completed';
    case 'cancelled':   return 'Cancelled';
    case 'uploaded':    return 'Uploaded';
    default:            return status;
  }
}

function displayStatus(a: DmActivity): string {
  // Cancelled is permanent and also moved to storage — check it before 'uploaded'.
  if (a.status === 'cancelled') return 'cancelled';
  if (a.is_in_storage || !!a.upload_link) return 'uploaded';
  // A pending activity whose day has passed was never completed.
  if (a.status === 'pending' && format(parseISO(a.date_time), 'yyyy-MM-dd') < format(new Date(), 'yyyy-MM-dd')) {
    return 'overdue';
  }
  return a.status;
}

function statusAccentClass(status: string): string {
  switch (status) {
    case 'overdue':   return 'bg-rose-500';
    case 'completed': return 'bg-yellow-400';
    case 'cancelled': return 'bg-zinc-400';
    case 'uploaded':  return 'bg-emerald-500';
    default:          return 'bg-slate-300 dark:bg-slate-600';
  }
}

// ── Status dropdown ───────────────────────────────────────────────────────────

const STATUS_LABELS: Record<DmActivityStatus, string> = {
  pending: 'Pending',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

type StatusSelectSize = 'chip' | 'row' | 'full';

const STATUS_TRIGGER_SIZE: Record<StatusSelectSize, string> = {
  // Chip: icon-only trigger; the label is kept for screen readers.
  chip: 'h-6 w-6 shrink-0 justify-center rounded px-0 py-0 sm:h-6 sm:px-0 sm:py-0 border-0 bg-transparent hover:bg-black/5 dark:hover:bg-white/10 [&>span]:sr-only [&>svg]:h-4 [&>svg]:w-4 [&>svg]:opacity-80',
  row: 'h-6 w-28 shrink-0 rounded-full px-2 py-0 text-[11px] font-medium sm:h-6 sm:px-2 sm:py-0',
  full: 'h-8 w-full sm:h-9 text-sm font-medium',
};

function StatusSelect({ activity, size, onChange, onOpenChange }: {
  activity: DmActivity;
  size: StatusSelectSize;
  onChange: (activity: DmActivity, status: DmActivityStatus) => void;
  onOpenChange?: (open: boolean) => void;
}) {
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  const colorClass = size === 'chip' ? '' : `border ${statusBadgeClass(displayStatus(activity))}`;
  return (
    // Stops clicks (also from the portalled menu) reaching the chip, row or day cell behind it.
    <span className="contents" onClick={stop} onKeyDown={stop}>
      <Select value={activity.status} onValueChange={v => onChange(activity, v as DmActivityStatus)} onOpenChange={onOpenChange}>
        <SelectTrigger
          title="Change status"
          aria-label={`Change status of ${activity.title}`}
          className={`${STATUS_TRIGGER_SIZE[size]} ${colorClass} focus:ring-1 focus:ring-offset-0`}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {(Object.keys(STATUS_LABELS) as DmActivityStatus[]).map(s => (
            <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </span>
  );
}

// ── Actions on a completed activity (same set as the Activities table) ───────

type PostActionKind = 'schedule' | 'upload' | 'swap';

const POST_ACTIONS: { kind: PostActionKind; label: string; icon: typeof Clock }[] = [
  { kind: 'schedule', label: 'Schedule',    icon: Calendar },
  { kind: 'upload',   label: 'Upload Link', icon: Upload },
  { kind: 'swap',     label: 'Swap Page',   icon: ArrowLeftRight },
];

// Chip-sized menu for a completed activity — sits where the status dropdown sits on pending chips.
function PostActionMenu({ activity, onSelect, onOpenChange }: {
  activity: DmActivity;
  onSelect: (activity: DmActivity, kind: PostActionKind) => void;
  onOpenChange?: (open: boolean) => void;
}) {
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  return (
    // Stops clicks (also from the portalled menu) reaching the chip, row or day cell behind it.
    <span className="contents" onClick={stop} onKeyDown={stop}>
      <DropdownMenu onOpenChange={onOpenChange}>
        <DropdownMenuTrigger
          title="Actions"
          aria-label={`Actions for ${activity.title}`}
          className="h-6 w-6 shrink-0 inline-flex items-center justify-center rounded hover:bg-black/5 dark:hover:bg-white/10 focus:outline-none focus-visible:ring-1"
        >
          <ChevronDown className="h-4 w-4 opacity-80" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {POST_ACTIONS.map(({ kind, label, icon: Icon }) => (
            <DropdownMenuItem key={kind} onSelect={() => onSelect(activity, kind)}>
              <Icon className="mr-2 h-3.5 w-3.5" />
              {label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}

// ── Shared modal pieces ───────────────────────────────────────────────────────

const MODAL_CONTENT_CLASS = 'max-w-md p-0 sm:p-0 gap-0 overflow-hidden';
const MODAL_FOOTER_CLASS = 'flex items-center gap-2 border-t bg-muted/30 px-6 py-3';
// One look for every control in the toolbar (month switcher, Today, page filter): 36px, same border.
const TOOLBAR_CONTROL_CLASS = 'h-9 sm:h-9 rounded-md border border-slate-200 bg-white/80 shadow-sm dark:border-border dark:bg-card';
const ACTION_BUTTON_CLASS = 'border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800 hover:border-emerald-300 dark:border-emerald-900/50 dark:bg-emerald-900/30 dark:text-emerald-400 dark:hover:bg-emerald-900/50 dark:hover:text-emerald-300';
const PILL_CLASS = 'rounded-full border border-teal-200 bg-teal-50 px-2 py-0.5 text-xs font-medium text-teal-700 dark:border-teal-900/50 dark:bg-teal-900/30 dark:text-teal-400';

function CalendarModalHeader({ accentClass, eyebrow, title }: {
  accentClass: string;
  eyebrow: ReactNode;
  title: ReactNode;
}) {
  return (
    <>
      <div className={`h-1.5 w-full ${accentClass}`} />
      <DialogHeader className="px-6 pt-5 pb-1 space-y-2 text-left">
        <div className="self-start">{eyebrow}</div>
        <DialogTitle className="text-lg font-semibold leading-snug pr-8 break-words">{title}</DialogTitle>
      </DialogHeader>
    </>
  );
}

// ── Activity detail dialog ────────────────────────────────────────────────────

function DetailRow({ icon: Icon, label, children }: {
  icon: typeof Clock;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-teal-200/80 bg-teal-50 text-teal-600 dark:border-teal-800/60 dark:bg-teal-900/30 dark:text-teal-400">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
        <div className="mt-0.5 text-sm text-foreground">{children}</div>
      </div>
    </div>
  );
}

function ActivityDetailDialog({
  activity, open, onClose, canDelete, onDelete, canEdit, onEdit, canChangeStatus, onStatusChange, onPostAction,
}: {
  activity: DmActivity;
  open: boolean;
  onClose: () => void;
  canDelete: boolean;
  onDelete: () => void;
  canEdit: boolean;
  onEdit: () => void;
  canChangeStatus: boolean;
  onStatusChange: (activity: DmActivity, status: DmActivityStatus) => void;
  onPostAction: ((kind: PostActionKind) => void) | null;
}) {
  const [copied, setCopied] = useState(false);
  const isCompleted = activity.status === 'completed';
  const status = displayStatus(activity);
  const locked = isActivityLocked(activity);
  const showFooter = !isCompleted && (canEdit || canDelete || locked);

  async function copyLocation() {
    if (!activity.location) return;
    try {
      await navigator.clipboard.writeText(activity.location);
      setCopied(true);
      toast.success('Location copied');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy location');
    }
  }

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className={MODAL_CONTENT_CLASS}>
        <CalendarModalHeader
          accentClass={statusAccentClass(status)}
          eyebrow={
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusBadgeClass(status)}`}>
              {statusLabel(status)}
            </span>
          }
          title={activity.title}
        />

        <div className="px-6 py-4 space-y-3.5">
          {canChangeStatus && (
            <DetailRow icon={CircleDot} label="Status">
              <StatusSelect activity={activity} size="full" onChange={onStatusChange} />
            </DetailRow>
          )}
          {activity.shooting_category && (
            <DetailRow icon={BookOpen} label="Category">{activity.shooting_category.name}</DetailRow>
          )}
          <DetailRow icon={Clock} label="Date & Time">
            {format(new Date(activity.date_time), 'EEE, d MMM yyyy · HH:mm')}
          </DetailRow>
          <DetailRow icon={MapPin} label="Location">
            {activity.location ? (
              <div className="flex items-start gap-1.5">
                <code className="flex-1 min-w-0 rounded-md border bg-muted/60 px-2.5 py-1.5 font-mono text-xs leading-relaxed break-all">
                  {activity.location}
                </code>
                <Button
                  variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
                  title="Copy location" onClick={copyLocation}
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                </Button>
              </div>
            ) : (
              <span className="text-rose-500">No location added</span>
            )}
          </DetailRow>
          {activity.page && (
            <DetailRow icon={Calendar} label="Page Section">{activity.page.name}</DetailRow>
          )}
          {activity.assigned_users.length > 0 && (
            <DetailRow icon={Users} label="Assigned To">
              <div className="flex flex-wrap gap-1.5">
                {activity.assigned_users.map(u => (
                  <span key={u.id} className={PILL_CLASS}>
                    {u.first_name} {u.last_name}
                  </span>
                ))}
              </div>
            </DetailRow>
          )}
          {activity.scheduled_date && (
            <DetailRow icon={Clock} label="Scheduled">
              {format(new Date(activity.scheduled_date), 'EEE, d MMM yyyy · HH:mm')}
            </DetailRow>
          )}
        </div>

        {onPostAction && (
          <div className="grid grid-cols-3 gap-1.5 sm:gap-2 border-t bg-muted/30 px-4 sm:px-6 py-3">
            {POST_ACTIONS.map(({ kind, label, icon: Icon }) => (
              <Button
                key={kind} variant="ghost" size="sm"
                className={`h-9 w-full min-w-0 justify-center gap-1 sm:gap-1.5 px-1.5 text-xs sm:text-sm whitespace-nowrap sm:h-9 sm:px-2 ${ACTION_BUTTON_CLASS}`}
                onClick={() => onPostAction(kind)}
              >
                <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                <span className="truncate">{label}</span>
              </Button>
            ))}
          </div>
        )}

        {showFooter && (
          <div className={`${MODAL_FOOTER_CLASS} justify-end`}>
            {locked && (
              <span className="mr-auto flex items-center gap-1.5 text-xs text-muted-foreground">
                <Lock className="w-3.5 h-3.5" /> {activityLockHint(activity)}
              </span>
            )}
            {canDelete && (
              <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive" onClick={onDelete}>
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
              </Button>
            )}
            {canEdit && !locked && (
              <Button size="sm" onClick={onEdit}>
                <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Day activities dialog ─────────────────────────────────────────────────────

function DayActivitiesDialog({
  date, activities, open, onClose, onSelect, canChangeStatus, onStatusChange, canPostAction, onPostAction,
}: {
  date: string;
  activities: DmActivity[];
  open: boolean;
  onClose: () => void;
  onSelect: (activity: DmActivity) => void;
  canChangeStatus: (activity: DmActivity) => boolean;
  onStatusChange: (activity: DmActivity, status: DmActivityStatus) => void;
  canPostAction: (activity: DmActivity) => boolean;
  onPostAction: (activity: DmActivity, kind: PostActionKind) => void;
}) {
  const sorted = [...activities].sort((a, b) => a.date_time.localeCompare(b.date_time));
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className={MODAL_CONTENT_CLASS}>
        <CalendarModalHeader
          accentClass="bg-teal-500"
          eyebrow={
            <span className={PILL_CLASS}>
              {sorted.length} {sorted.length === 1 ? 'activity' : 'activities'}
            </span>
          }
          title={format(parseISO(date), 'EEEE, d MMM yyyy')}
        />

        <div className="min-w-0 px-6 py-4">
          <div className="space-y-2 max-h-[60vh] overflow-y-auto -mr-2 pr-2">
            {sorted.map(a => {
              const status = displayStatus(a);
              const meta = [a.page?.name, a.shooting_category?.name].filter(Boolean).join(' · ');
              return (
                <div
                  key={a.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelect(a)}
                  onKeyDown={e => {
                    if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      onSelect(a);
                    }
                  }}
                  className="relative w-full overflow-hidden text-left rounded-lg border bg-card pl-4 pr-3 py-2.5 flex items-center gap-3 cursor-pointer transition-colors hover:bg-muted/50 hover:border-teal-300 dark:hover:border-teal-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className={`absolute inset-y-0 left-0 w-[3px] ${statusAccentClass(status)}`} />
                  <span className="flex h-8 w-12 shrink-0 items-center justify-center rounded-md bg-teal-50 text-xs font-semibold text-teal-700 dark:bg-teal-900/30 dark:text-teal-400">
                    {format(new Date(a.date_time), 'HH:mm')}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-start gap-1 text-sm font-medium text-foreground">
                      {!a.location && <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5 text-rose-400" />}
                      <span className="break-words">{a.title}</span>
                    </span>
                    {meta && <span className="block text-xs text-muted-foreground mt-0.5 truncate">{meta}</span>}
                  </span>
                  <span className="shrink-0 flex flex-col items-end gap-1">
                    {canChangeStatus(a) ? (
                      <StatusSelect activity={a} size="row" onChange={onStatusChange} />
                    ) : (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${statusBadgeClass(status)}`}>
                        {statusLabel(status)}
                      </span>
                    )}
                    {canPostAction(a) && (
                      <span className="flex items-center gap-0.5">
                        {POST_ACTIONS.map(({ kind, label, icon: Icon }) => (
                          <Button
                            key={kind}
                            variant="ghost" size="icon"
                            className={`h-7 w-7 min-h-0 min-w-0 rounded-md p-0 sm:h-7 sm:w-7 ${ACTION_BUTTON_CLASS}`}
                            title={label} aria-label={`${label}: ${a.title}`}
                            onClick={e => { e.stopPropagation(); onPostAction(a, kind); }}
                            onKeyDown={e => e.stopPropagation()}
                          >
                            <Icon className="w-4 h-4" />
                          </Button>
                        ))}
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className={MODAL_FOOTER_CLASS}>
          <p className="text-xs text-muted-foreground">Click an activity to view details</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Complete Activity Dialog ──────────────────────────────────────────────────

const NO_PAGE = '__none__';

function CompleteCalendarActivityDialog({ activity, open, onClose, pages }: {
  activity: DmActivity;
  open: boolean;
  onClose: () => void;
  pages: { id: string; name: string }[];
}) {
  const qc = useQueryClient();
  const initialPageId = activity.updated_page?.id ?? NO_PAGE;
  const [selectedPageId, setSelectedPageId] = useState(initialPageId);
  const [pageError, setPageError] = useState(false);

  const statusMutation = useMutation({
    mutationFn: () => dmTeamService.updateActivityStatus(activity.id, { status: 'completed' }),
  });
  const pageMutation = useMutation({
    mutationFn: (pageId: string) => dmTeamService.updateActivity(activity.id, { updated_page_id: pageId }),
  });

  const locationMissing = !activity.location?.trim();
  const isPending = statusMutation.isPending || pageMutation.isPending;

  async function handleConfirm() {
    if (locationMissing) {
      toast.error('Location is required before completing this activity');
      return;
    }
    if (selectedPageId === NO_PAGE) {
      setPageError(true);
      return;
    }
    try {
      await statusMutation.mutateAsync();
      if (selectedPageId !== initialPageId) await pageMutation.mutateAsync(selectedPageId);
      toast.success('Activity marked as completed');
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message ?? 'Failed to complete activity');
    } finally {
      qc.invalidateQueries({ queryKey: ['dm-team', 'calendar'] });
      qc.invalidateQueries({ queryKey: ['dm-team', 'activities'] });
    }
  }

  return (
    <Dialog open={open} onOpenChange={v => !v && !isPending && onClose()}>
      <DialogContent className={MODAL_CONTENT_CLASS}>
        <CalendarModalHeader
          accentClass={statusAccentClass('completed')}
          eyebrow={
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusBadgeClass('completed')}`}>
              Complete Activity
            </span>
          }
          title={activity.title}
        />

        <div className="px-6 py-4 space-y-4">
          {locationMissing && (
            <div className="flex items-start gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2.5 text-sm text-rose-600 dark:text-rose-400">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>Location is required. Edit the activity to add a location before completing.</span>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Current Page Section</Label>
            <p className="text-sm text-muted-foreground rounded-md bg-muted px-3 py-2">
              {activity.page?.name ?? '—'}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label>Update Page *</Label>
            <Select value={selectedPageId} onValueChange={v => { setSelectedPageId(v); setPageError(false); }}>
              <SelectTrigger className={pageError ? 'border-destructive focus:ring-destructive' : ''}>
                <SelectValue placeholder="Select page…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_PAGE}>— Select a page —</SelectItem>
                {pages.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {pageError && <p className="text-xs text-destructive">Page selection is required</p>}
          </div>
        </div>

        <div className={`${MODAL_FOOTER_CLASS} justify-end`}>
          <Button variant="outline" size="sm" onClick={onClose} disabled={isPending}>Cancel</Button>
          <Button size="sm" onClick={handleConfirm} disabled={isPending || locationMissing}>
            {isPending ? 'Completing…' : 'Confirm Complete'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Completed-activity action dialogs ─────────────────────────────────────────

function invalidateActivityQueries(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['dm-team', 'calendar'] });
  qc.invalidateQueries({ queryKey: ['dm-team', 'activities'] });
  qc.invalidateQueries({ queryKey: ['dm-team', 'storage'] });
}

function PostActionShell({ activity, eyebrow, onClose, isPending, canSave, saveLabel, onSave, children }: {
  activity: DmActivity;
  eyebrow: string;
  onClose: () => void;
  isPending: boolean;
  canSave: boolean;
  saveLabel: string;
  onSave: () => void;
  children: ReactNode;
}) {
  return (
    <Dialog open onOpenChange={v => !v && !isPending && onClose()}>
      <DialogContent className={MODAL_CONTENT_CLASS}>
        <CalendarModalHeader
          accentClass="bg-teal-500"
          eyebrow={<span className={PILL_CLASS}>{eyebrow}</span>}
          title={activity.title}
        />
        <div className="px-6 py-4 space-y-4">{children}</div>
        <div className={`${MODAL_FOOTER_CLASS} justify-end`}>
          <Button variant="outline" size="sm" onClick={onClose} disabled={isPending}>Cancel</Button>
          <Button size="sm" onClick={onSave} disabled={isPending || !canSave}>
            {isPending ? 'Saving…' : saveLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ScheduleCalendarActivityDialog({ activity, onClose }: { activity: DmActivity; onClose: () => void }) {
  const qc = useQueryClient();
  // Pre-fill only a scheduled date that is still today or later; past days can't be picked.
  const [date, setDate] = useState(
    activity.scheduled_date && new Date(activity.scheduled_date) >= startOfToday()
      ? format(new Date(activity.scheduled_date), "yyyy-MM-dd'T'HH:mm")
      : '',
  );
  const mutation = useMutation({
    mutationFn: () => dmTeamService.scheduleActivity(activity.id, { scheduled_date: localToISO(date) }),
    onSuccess: () => { invalidateActivityQueries(qc); toast.success('Activity scheduled'); onClose(); },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Failed to schedule'),
  });

  return (
    <PostActionShell
      activity={activity} eyebrow="Schedule Activity" onClose={onClose}
      isPending={mutation.isPending} canSave={!!date} saveLabel="Schedule" onSave={() => mutation.mutate()}
    >
      <div className="space-y-1.5">
        <Label>Scheduled Date & Time</Label>
        <DateTimePicker value={date || null} onChange={v => setDate(v || '')} minDate={startOfToday()} />
      </div>
    </PostActionShell>
  );
}

function UploadLinkCalendarDialog({ activity, onClose }: { activity: DmActivity; onClose: () => void }) {
  const qc = useQueryClient();
  const [link, setLink] = useState(activity.upload_link ?? '');
  const mutation = useMutation({
    mutationFn: async () => {
      await dmTeamService.uploadActivity(activity.id, { upload_link: link.trim() });
      await dmTeamService.moveToStorage(activity.id, true);
    },
    onSuccess: () => { toast.success('Upload link saved & moved to storage'); onClose(); },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Failed to save upload link'),
    // Refresh even on failure: the link may be saved while the storage move failed.
    onSettled: () => invalidateActivityQueries(qc),
  });

  return (
    <PostActionShell
      activity={activity} eyebrow="Upload Link" onClose={onClose}
      isPending={mutation.isPending} canSave={!!link.trim()} saveLabel="Save" onSave={() => mutation.mutate()}
    >
      <div className="space-y-1.5">
        <Label>Upload URL</Label>
        <Input value={link} onChange={e => setLink(e.target.value)} placeholder="https://…" autoFocus />
        <p className="text-xs text-muted-foreground">Saving moves this activity to Storage.</p>
      </div>
    </PostActionShell>
  );
}

function SwapPageCalendarDialog({ activity, onClose, pages }: {
  activity: DmActivity;
  onClose: () => void;
  pages: { id: string; name: string }[];
}) {
  const qc = useQueryClient();
  const current = activity.updated_page ?? activity.page;
  const [pageId, setPageId] = useState(current?.id ?? '');
  const mutation = useMutation({
    mutationFn: () => dmTeamService.updateActivity(activity.id, { updated_page_id: pageId }),
    onSuccess: () => { invalidateActivityQueries(qc); toast.success('Page updated'); onClose(); },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Failed to update page'),
  });

  return (
    <PostActionShell
      activity={activity} eyebrow="Swap Page" onClose={onClose}
      isPending={mutation.isPending} canSave={!!pageId && pageId !== current?.id}
      saveLabel="Confirm" onSave={() => mutation.mutate()}
    >
      <div className="space-y-1.5">
        <Label>Current Page</Label>
        <p className="text-sm text-muted-foreground rounded-md bg-muted px-3 py-2">{current?.name ?? '—'}</p>
      </div>
      <div className="space-y-1.5">
        <Label>New Page</Label>
        <Select value={pageId} onValueChange={setPageId}>
          <SelectTrigger><SelectValue placeholder="Select page…" /></SelectTrigger>
          <SelectContent>
            {pages.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </PostActionShell>
  );
}

// ── Create Activity Dialog ────────────────────────────────────────────────────

const PAGE_SECTION_REQUIRED = 'Page section is required';

const calendarActivityFormSchema = createDmActivitySchema.extend({
  date_time: z.string().min(1, 'Date & time is required'),
  page_id: z.string({ required_error: PAGE_SECTION_REQUIRED }).uuid(PAGE_SECTION_REQUIRED),
});
type CalendarActivityFormData = z.infer<typeof calendarActivityFormSchema>;

function localToISO(v: string): string {
  if (!v) return v;
  return new Date(v.length === 16 ? v + ':00' : v).toISOString();
}

function CreateActivityDialog({
  open, onClose, defaultDate, shootingCategories, pages, assignList,
}: {
  open: boolean;
  onClose: () => void;
  defaultDate?: string;
  shootingCategories: any[];
  pages: any[];
  assignList: DmUserRef[];
}) {
  const qc = useQueryClient();
  const form = useForm<CalendarActivityFormData>({ resolver: zodResolver(calendarActivityFormSchema) });
  const [assignedUserId, setAssignedUserId] = useState('');

  useEffect(() => {
    if (open) {
      form.setValue('date_time', defaultDate ?? '');
    } else {
      setAssignedUserId('');
    }
  }, [open, defaultDate]);

  const mutation = useMutation({
    mutationFn: (body: CreateDmActivityInput) => dmTeamService.createActivity(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dm-team', 'calendar'] });
      onClose();
      form.reset();
      toast.success('Activity added to calendar');
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Failed to create'),
  });

  const catId = form.watch('shooting_category_id');
  const selectedCat = shootingCategories.find(c => c.id === catId);
  const requiresLocation = selectedCat?.requires_location ?? false;

  function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (requiresLocation && !form.getValues('location')?.trim()) {
      form.setError('location', { message: 'Location is required for this category' });
    }
    form.handleSubmit((d) => {
      if (requiresLocation && !d.location?.trim()) return;
      mutation.mutate({
        ...d,
        date_time: localToISO(d.date_time),
        assigned_user_ids: assignedUserId ? [assignedUserId] : undefined,
      } as CreateDmActivityInput);
    })(e);
  }

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Add Calendar Activity</DialogTitle></DialogHeader>
        <form
          onSubmit={handleFormSubmit}
          className="space-y-4"
        >
          <div><Label>Title *</Label><Input {...form.register('title')} className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category *</Label>
              <Select onValueChange={v => form.setValue('shooting_category_id', v)}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {shootingCategories.map((c: any) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date & Time *</Label>
              <DateTimePicker
                value={form.watch('date_time') || null}
                onChange={v => form.setValue('date_time', v || '', { shouldValidate: true })}
                minDate={startOfToday()}
              />
              {form.formState.errors.date_time && (
                <p className="text-xs text-destructive mt-1">{form.formState.errors.date_time.message}</p>
              )}
            </div>
          </div>
          <div>
            <Label>Location {requiresLocation ? '*' : '(optional)'}</Label>
            <Input {...form.register('location')} className="mt-1" placeholder="Location / NAS path…" />
            {form.formState.errors.location && (
              <p className="text-xs text-destructive mt-1">{form.formState.errors.location.message}</p>
            )}
          </div>
          <div>
            <Label>Assign User (Optional)</Label>
            <Select value={assignedUserId} onValueChange={setAssignedUserId}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Select user…" /></SelectTrigger>
              <SelectContent>
                {assignList.map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Page Section *</Label>
            <Select onValueChange={v => form.setValue('page_id', v, { shouldValidate: true })}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Select page…" /></SelectTrigger>
              <SelectContent>
                {pages.map((p: any) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {form.formState.errors.page_id && (
              <p className="text-xs text-destructive mt-1">{form.formState.errors.page_id.message}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending || (requiresLocation && !form.watch('location')?.trim())}>Add Activity</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Edit Activity Dialog ──────────────────────────────────────────────────────

const editCalendarActivityFormSchema = updateDmActivitySchema.extend({
  date_time: z.string().min(1, 'Date & time is required'),
  updated_page_id: z.string({ required_error: PAGE_SECTION_REQUIRED }).uuid(PAGE_SECTION_REQUIRED),
});
type EditCalendarActivityFormData = z.infer<typeof editCalendarActivityFormSchema>;

function EditCalendarActivityDialog({
  activity, open, onClose, onSubmit, isPending, pages, categories,
}: {
  activity: DmActivity | null;
  open: boolean;
  onClose: () => void;
  onSubmit: (body: UpdateDmActivityInput) => void;
  isPending: boolean;
  pages: any[];
  categories: any[];
}) {
  const form = useForm<EditCalendarActivityFormData>({ resolver: zodResolver(editCalendarActivityFormSchema) });

  useEffect(() => {
    if (open && activity) {
      form.reset({
        title: activity.title,
        shooting_category_id: activity.shooting_category?.id ?? '',
        location: activity.location ?? '',
        date_time: activity.date_time
          ? format(new Date(activity.date_time), "yyyy-MM-dd'T'HH:mm")
          : '',
        updated_page_id: activity.updated_page?.id ?? activity.page?.id ?? '',
      });
    }
  }, [open, activity]);

  const catId = form.watch('shooting_category_id');
  const requiresLocation = categories.find(c => c.id === catId)?.requires_location ?? false;

  if (!activity) return null;

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Edit Activity</DialogTitle></DialogHeader>
        <form
          onSubmit={form.handleSubmit(d =>
            onSubmit({ ...d, date_time: localToISO(d.date_time) } as UpdateDmActivityInput)
          )}
          className="space-y-4"
        >
          <div><Label>Title *</Label><Input {...form.register('title')} className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select
                defaultValue={activity.shooting_category?.id ?? ''}
                onValueChange={v => form.setValue('shooting_category_id', v)}
              >
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c: any) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date & Time *</Label>
              <DateTimePicker
                value={form.watch('date_time') || null}
                onChange={v => form.setValue('date_time', v || '', { shouldValidate: true })}
                minDate={startOfToday()}
              />
              {form.formState.errors.date_time && (
                <p className="text-xs text-destructive mt-1">{form.formState.errors.date_time.message}</p>
              )}
            </div>
          </div>
          <div>
            <Label>Location {requiresLocation ? '*' : '(optional)'}</Label>
            <Input {...form.register('location')} className="mt-1" placeholder="Location…" />
            {form.formState.errors.location && (
              <p className="text-xs text-destructive mt-1">{form.formState.errors.location.message}</p>
            )}
          </div>
          <div>
            <Label>Page Section *</Label>
            <Select
              defaultValue={activity.updated_page?.id ?? activity.page?.id ?? ''}
              onValueChange={v => form.setValue('updated_page_id', v, { shouldValidate: true })}
            >
              <SelectTrigger className="mt-1"><SelectValue placeholder="Select page…" /></SelectTrigger>
              <SelectContent>
                {pages.map((p: any) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {form.formState.errors.updated_page_id && (
              <p className="text-xs text-destructive mt-1">{form.formState.errors.updated_page_id.message}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={isPending || (requiresLocation && !form.watch('location')?.trim())}>Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Calendar Grid ─────────────────────────────────────────────────────────────

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const CHIPS_PER_CELL = 3;
const HOVER_CLOSE_DELAY_MS = 120;

function DayChip({ activity, canChangeStatus, onView, onStatusChange, canPostAction, onPostAction, onMenuOpenChange }: {
  activity: DmActivity;
  canChangeStatus: boolean;
  onView: (activity: DmActivity) => void;
  onStatusChange: (activity: DmActivity, status: DmActivityStatus) => void;
  canPostAction: boolean;
  onPostAction: (activity: DmActivity, kind: PostActionKind) => void;
  onMenuOpenChange?: (open: boolean) => void;
}) {
  const pageName = (activity.updated_page ?? activity.page)?.name;
  return (
    <div className={`w-full text-[11px] font-medium rounded-md border transition-colors flex items-center min-w-0 ${activityChipClass(displayStatus(activity))}`}>
      <button
        onClick={e => { e.stopPropagation(); onView(activity); }}
        className="flex-1 min-w-0 text-left px-1.5 py-0.5 flex flex-col items-stretch"
      >
        <span className="flex items-center gap-0.5 min-w-0">
          {!activity.location && <MapPin className="w-2.5 h-2.5 shrink-0 opacity-70 text-rose-400" />}
          <span className="truncate">{activity.title}</span>
        </span>
        {pageName && <span className="truncate text-[10px] font-normal opacity-70">{pageName}</span>}
      </button>
      {canChangeStatus ? (
        <StatusSelect activity={activity} size="chip" onChange={onStatusChange} onOpenChange={onMenuOpenChange} />
      ) : canPostAction && (
        <PostActionMenu activity={activity} onSelect={onPostAction} onOpenChange={onMenuOpenChange} />
      )}
    </div>
  );
}

function CalendarGrid({
  year, month, activities, onAddActivity, onViewActivity, onViewDay, canChangeStatus, onStatusChange,
  canPostAction, onPostAction,
}: {
  year: number;
  month: number;
  activities: DmActivity[];
  onAddActivity: (dateStr: string) => void;
  onViewActivity: (activity: DmActivity) => void;
  onViewDay: (date: string) => void;
  canChangeStatus: (activity: DmActivity) => boolean;
  onStatusChange: (activity: DmActivity, status: DmActivityStatus) => void;
  canPostAction: (activity: DmActivity) => boolean;
  onPostAction: (activity: DmActivity, kind: PostActionKind) => void;
}) {
  const monthStart = startOfMonth(new Date(year, month));
  const monthEnd = endOfMonth(monthStart);
  const calStart = startOfWeek(monthStart);
  const calEnd = endOfWeek(monthEnd);
  const days = eachDayOfInterval({ start: calStart, end: calEnd });
  const todayKey = format(new Date(), 'yyyy-MM-dd');

  const byDay = activities.reduce<Record<string, DmActivity[]>>((acc, a) => {
    const key = format(parseISO(a.date_time), 'yyyy-MM-dd');
    if (!acc[key]) acc[key] = [];
    acc[key].push(a);
    return acc;
  }, {});

  // ── Hover-grow: a busy day expands into a scrollable list of all its activities ──
  const [hoverDay, setHoverDay] = useState<string | null>(null);
  const closeTimer = useRef<number | null>(null);
  const menuOpen = useRef(false);
  const removeMoveListener = useRef<(() => void) | null>(null);

  function cancelClose() {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }
  function scheduleClose() {
    cancelClose();
    closeTimer.current = window.setTimeout(() => {
      if (!menuOpen.current) setHoverDay(null);
    }, HOVER_CLOSE_DELAY_MS);
  }
  function enterDay(key: string, count: number) {
    if (count <= CHIPS_PER_CELL) return;
    menuOpen.current = false;
    cancelClose();
    setHoverDay(key);
  }
  // The status menu is portalled outside the cell, so moving onto it fires mouseleave.
  // Keep the panel open while it is open; afterwards close on the first move outside the cell.
  function handleMenuOpenChange(key: string, open: boolean) {
    menuOpen.current = open;
    removeMoveListener.current?.();
    removeMoveListener.current = null;
    if (open) { cancelClose(); return; }
    const onMove = (e: PointerEvent) => {
      const cell = document.querySelector(`[data-calendar-day="${key}"]`);
      if (!cell || !(e.target instanceof Node) || !cell.contains(e.target)) scheduleClose();
      removeMoveListener.current?.();
      removeMoveListener.current = null;
    };
    document.addEventListener('pointermove', onMove);
    removeMoveListener.current = () => document.removeEventListener('pointermove', onMove);
  }
  useEffect(() => () => { cancelClose(); removeMoveListener.current?.(); }, []);

  return (
    <div className="rounded-2xl border border-slate-200/80 dark:border-border/80 bg-white/60 dark:bg-card/60 backdrop-blur-sm overflow-hidden">
      {/* Weekday header */}
      <div className="grid grid-cols-7 bg-gradient-to-r from-teal-50 to-teal-50/40 dark:from-teal-900/30 dark:to-teal-900/10 border-b border-teal-100 dark:border-teal-900/30">
        {WEEKDAYS.map(d => (
          <div key={d} className="px-2 py-3 text-[11px] font-semibold text-teal-700 dark:text-teal-400 text-center uppercase tracking-wider">
            {d}
          </div>
        ))}
      </div>
      {/* Day cells */}
      <div className="grid grid-cols-7">
        {days.map((day, index) => {
          const key = format(day, 'yyyy-MM-dd');
          const dayActivities = byDay[key] ?? [];
          const isCurrentMonth = isSameMonth(day, monthStart);
          const isToday = isSameDay(day, new Date());
          const isGrown = hoverDay === key;
          // Past days can't take new activities (the date picker blocks them too).
          const canAddHere = isCurrentMonth && key >= todayKey;
          // Last week row grows upward so the grid's rounded clip doesn't cut the panel.
          const growUp = index >= days.length - WEEKDAYS.length;

          return (
            <div
              key={key}
              data-calendar-day={key}
              onClick={dayActivities.length > 0 ? () => onViewDay(key) : undefined}
              onMouseEnter={() => enterDay(key, dayActivities.length)}
              onMouseLeave={scheduleClose}
              className={`group relative min-h-[110px] ${dayActivities.length > 0 ? 'cursor-pointer' : ''} border-b border-r border-slate-100/80 dark:border-border/50 p-2 transition-colors ${
                isCurrentMonth
                  ? 'bg-white/80 dark:bg-muted/80 hover:bg-white/95 dark:hover:bg-muted'
                  : 'bg-slate-50/40 dark:bg-card/40 opacity-50'
              }`}
            >
              <div className="flex items-start justify-between mb-1.5">
                <span className={
                  isToday
                    ? 'w-7 h-7 rounded-full bg-teal-600 text-white text-xs font-bold flex items-center justify-center shadow-sm shadow-teal-200'
                    : isCurrentMonth
                      ? 'text-sm font-semibold text-slate-700 dark:text-slate-300 w-7 h-7 flex items-center justify-center'
                      : 'text-xs text-slate-400 dark:text-slate-600 w-7 h-7 flex items-center justify-center'
                }>
                  {format(day, 'd')}
                </span>
                {canAddHere && (
                  <button
                    title="Add activity"
                    aria-label={`Add activity on ${format(day, 'd MMM')}`}
                    onClick={e => { e.stopPropagation(); onAddActivity(key + 'T09:00'); }}
                    className={`h-6 w-6 flex items-center justify-center rounded-md transition-all opacity-0 group-hover:opacity-100 focus-visible:opacity-100 ${ACTION_BUTTON_CLASS}`}
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <div className="space-y-0.5">
                {dayActivities.slice(0, CHIPS_PER_CELL).map(a => (
                  <DayChip
                    key={a.id}
                    activity={a}
                    canChangeStatus={canChangeStatus(a)}
                    onView={onViewActivity}
                    onStatusChange={onStatusChange}
                    canPostAction={canPostAction(a)}
                    onPostAction={onPostAction}
                  />
                ))}
                {dayActivities.length > CHIPS_PER_CELL && (
                  <button
                    onClick={e => { e.stopPropagation(); onViewDay(key); }}
                    className="text-[10px] text-teal-600 font-medium px-1.5 hover:underline"
                  >
                    +{dayActivities.length - CHIPS_PER_CELL} more
                  </button>
                )}
              </div>

              {isGrown && (
                <div
                  data-calendar-day-panel={key}
                  className={`hidden md:flex absolute inset-x-0 z-30 flex-col rounded-xl bg-white dark:bg-card p-2 shadow-xl shadow-teal-900/10 ring-1 ring-teal-200 dark:ring-teal-800 animate-in fade-in-0 zoom-in-95 duration-200 ${
                    growUp ? 'bottom-0 origin-bottom slide-in-from-bottom-1' : 'top-0 origin-top slide-in-from-top-1'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className={
                      isToday
                        ? 'w-7 h-7 rounded-full bg-teal-600 text-white text-xs font-bold flex items-center justify-center shadow-sm shadow-teal-200'
                        : 'text-sm font-semibold text-slate-700 dark:text-slate-300 w-7 h-7 flex items-center justify-center'
                    }>
                      {format(day, 'd')}
                    </span>
                    <div className="flex items-center gap-1">
                      <span className={`${PILL_CLASS} text-[10px]`}>{dayActivities.length} activities</span>
                      {canAddHere && (
                        <button
                          title="Add activity"
                          aria-label={`Add activity on ${format(day, 'd MMM')}`}
                          onClick={e => { e.stopPropagation(); onAddActivity(key + 'T09:00'); }}
                          className={`h-6 w-6 flex items-center justify-center rounded-md transition-colors ${ACTION_BUTTON_CLASS}`}
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="space-y-0.5 max-h-[220px] overflow-y-auto overscroll-contain pr-0.5 [scrollbar-width:thin]">
                    {dayActivities.map(a => (
                      <DayChip
                        key={a.id}
                        activity={a}
                        canChangeStatus={canChangeStatus(a)}
                        onView={onViewActivity}
                        onStatusChange={onStatusChange}
                        canPostAction={canPostAction(a)}
                        onPostAction={onPostAction}
                        onMenuOpenChange={open => handleMenuOpenChange(key, open)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Calendar Page ────────────────────────────────────────────────────────

export function DmCalendarPage() {
  const qc = useQueryClient();
  const permission = usePermission();
  const canCreate = permission.has(PERMISSIONS.DM_TEAM_CALENDAR_CREATE);
  const canDelete = permission.has(PERMISSIONS.DM_TEAM_CALENDAR_DELETE);
  const canManage = permission.has(PERMISSIONS.DM_TEAM_ACTIVITIES_MANAGE);

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  // Page sections ticked in the filter; empty = all.
  const [selectedPages, setSelectedPages] = useState<string[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [createDefaultDate, setCreateDefaultDate] = useState('');
  // Ids, not objects: dialogs read the latest row after a status change refetches.
  const [viewActivityId, setViewActivityId] = useState<string | null>(null);
  const [viewDayDate, setViewDayDate] = useState<string | null>(null);
  const [completeActivityId, setCompleteActivityId] = useState<string | null>(null);
  const [cancelActivity, setCancelActivity] = useState<DmActivity | null>(null);
  const [postAction, setPostAction] = useState<{ kind: PostActionKind; id: string } | null>(null);
  const [deleteActivity, setDeleteActivity] = useState<DmActivity | null>(null);
  const [editActivity, setEditActivity] = useState<DmActivity | null>(null);

  const { data: metaData } = useQuery({
    queryKey: ['dm-team', 'activities-meta'],
    queryFn: () => dmTeamService.getActivitiesMeta(),
  });
  const hasCalendarDeleteAccess = metaData?.data?.hasCalendarDeleteAccess ?? false;

  const { data: calData } = useQuery({
    queryKey: ['dm-team', 'calendar', { year, month }],
    queryFn: () => dmTeamService.getCalendarActivities({ year, month: month + 1 }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => dmTeamService.deleteCalendarActivity(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dm-team', 'calendar'] });
      setDeleteActivity(null);
      setViewActivityId(null);
      toast.success('Activity removed from calendar');
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Not permitted to delete'),
  });

  const editMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateDmActivityInput }) =>
      dmTeamService.updateActivity(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dm-team', 'calendar'] });
      setEditActivity(null);
      toast.success('Activity updated');
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Failed to update'),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: DmActivityStatus }) =>
      dmTeamService.updateActivityStatus(id, { status }),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ['dm-team', 'calendar'] });
      qc.invalidateQueries({ queryKey: ['dm-team', 'activities'] });
      qc.invalidateQueries({ queryKey: ['dm-team', 'storage'] });
      toast.success(vars.status === 'cancelled' ? 'Activity cancelled — moved to Storage' : 'Status updated');
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Failed to update status'),
  });

  // Past, never-completed activities are read-only (see activityLock.ts); this one gate covers
  // grid chips, the hover card, the popup and the day list.
  const canChangeStatus = (a: DmActivity) => canManage && a.status !== 'completed' && !isActivityLocked(a);

  function handleStatusChange(a: DmActivity, status: DmActivityStatus) {
    if (status === a.status) return;
    if (status === 'completed') {
      if (!a.location?.trim()) {
        toast.error('Location is required before completing this activity');
        return;
      }
      // One modal at a time: the complete dialog replaces the popup / day list.
      setViewActivityId(null);
      setViewDayDate(null);
      setCompleteActivityId(a.id);
    } else if (status === 'cancelled') {
      // Permanent: confirm first (the confirm replaces the popup / day list).
      setViewActivityId(null);
      setViewDayDate(null);
      setCancelActivity(a);
    } else {
      statusMutation.mutate({ id: a.id, status });
    }
  }

  const activities = calData?.data ?? [];
  // The month comes back whole, so the page-section filter runs here. Dialogs keep using the
  // unfiltered list so an open one doesn't vanish when the filter changes.
  const visibleActivities = selectedPages.length === 0
    ? activities
    : activities.filter(a => {
        // Same page as the chip shows: the one chosen later wins over the original.
        const page = a.updated_page ?? a.page;
        return page && selectedPages.includes(page.id);
      });
  const viewActivity = activities.find(a => a.id === viewActivityId) ?? null;
  const completeActivity = activities.find(a => a.id === completeActivityId) ?? null;
  const postActionActivity = activities.find(a => a.id === postAction?.id) ?? null;

  // Same rows the Activities table offers Schedule / Upload / Swap on: completed, not yet in storage.
  const canPostAction = (a: DmActivity) => canManage && a.status === 'completed' && !a.is_in_storage;

  function openPostAction(a: DmActivity, kind: PostActionKind) {
    setViewActivityId(null);
    setViewDayDate(null);
    setPostAction({ kind, id: a.id });
  }
  const viewDayActivities = viewDayDate
    ? activities.filter(a => format(parseISO(a.date_time), 'yyyy-MM-dd') === viewDayDate)
    : [];
  const pages = metaData?.data?.pages ?? [];
  const shootingCategories = metaData?.data?.shootingCategories ?? [];
  const assignList = metaData?.data?.assignList ?? [];

  const prevMonth = () => {
    if (month === 0) { setYear(y => y - 1); setMonth(11); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setYear(y => y + 1); setMonth(0); }
    else setMonth(m => m + 1);
  };

  return (
    <div className="space-y-4">

      {/* Hero header */}
      <div className="rounded-2xl bg-gradient-to-r from-teal-600 to-teal-500 px-6 py-5 text-white shadow-lg shadow-teal-200/50 flex items-center justify-between">
        <div>
          <p className="text-teal-100 text-sm font-medium">DM Team</p>
          <h1 className="text-2xl font-bold tracking-tight mt-0.5">Schedule Calendar</h1>
          <p className="text-teal-100/80 text-sm mt-1">Monthly activity overview</p>
        </div>
        {canCreate && (
          <Button
            onClick={() => { setCreateDefaultDate(''); setCreateOpen(true); }}
            className="bg-white text-teal-700 hover:bg-teal-50 shadow-sm font-semibold"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Add Activity
          </Button>
        )}
      </div>

      {/* Controls */}
      {/* relative z-20: backdrop-blur makes this a stacking context; it must sit above the grid so the filter panel isn't hidden. */}
      <div className="relative z-20 bg-white/70 dark:bg-card/70 backdrop-blur-sm rounded-xl border border-slate-200/80 dark:border-border/80 px-4 py-3 flex items-center gap-3 flex-wrap">
        {/* Month navigation */}
        <div className="flex items-center gap-2">
          <div className={`flex items-center overflow-hidden ${TOOLBAR_CONTROL_CLASS}`}>
            <Button
              variant="ghost" size="icon" aria-label="Previous month" title="Previous month"
              className="h-full w-9 sm:h-full sm:w-9 rounded-none text-slate-600 hover:bg-teal-50 hover:text-teal-700 dark:text-slate-300"
              onClick={prevMonth}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="min-w-[132px] border-x border-slate-200 dark:border-border px-3 text-center text-sm font-semibold tabular-nums text-slate-800 dark:text-slate-100">
              {format(new Date(year, month), 'MMMM yyyy')}
            </span>
            <Button
              variant="ghost" size="icon" aria-label="Next month" title="Next month"
              className="h-full w-9 sm:h-full sm:w-9 rounded-none text-slate-600 hover:bg-teal-50 hover:text-teal-700 dark:text-slate-300"
              onClick={nextMonth}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <Button
            variant="outline"
            className={`px-3 sm:px-3 text-sm font-medium ${TOOLBAR_CONTROL_CLASS} border-teal-200 text-teal-700 hover:bg-teal-50 hover:border-teal-300 hover:text-teal-800 dark:border-teal-800 dark:text-teal-400`}
            onClick={() => { setYear(today.getFullYear()); setMonth(today.getMonth()); }}
          >
            Today
          </Button>
        </div>

        {/* Page section filter */}
        <DmPageMultiSelect pages={pages} value={selectedPages} onChange={setSelectedPages} />

        {/* Legend */}
        <div className="flex min-h-9 items-center gap-x-3 gap-y-1 ml-auto flex-wrap">
          {[
            { label: 'New Entry',     dot: 'bg-slate-400' },
            { label: 'Not Completed', dot: 'bg-rose-500' },
            { label: 'Completed',   dot: 'bg-yellow-400' },
            { label: 'Uploaded',    dot: 'bg-emerald-400' },
            { label: 'Cancelled',   dot: 'bg-zinc-400' },
          ].map(({ label, dot }) => (
            <span key={label} className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${dot}`} />
              {label}
            </span>
          ))}
        </div>
      </div>

      <CalendarGrid
        year={year}
        month={month}
        activities={visibleActivities}
        onAddActivity={dateStr => { setCreateDefaultDate(dateStr); setCreateOpen(true); }}
        onViewActivity={a => setViewActivityId(a.id)}
        onViewDay={date => setViewDayDate(date)}
        canChangeStatus={canChangeStatus}
        onStatusChange={handleStatusChange}
        canPostAction={canPostAction}
        onPostAction={openPostAction}
      />

      {viewDayDate && viewDayActivities.length > 0 && (
        <DayActivitiesDialog
          date={viewDayDate}
          activities={viewDayActivities}
          open
          onClose={() => setViewDayDate(null)}
          onSelect={a => { setViewDayDate(null); setViewActivityId(a.id); }}
          canChangeStatus={canChangeStatus}
          onStatusChange={handleStatusChange}
          canPostAction={canPostAction}
          onPostAction={openPostAction}
        />
      )}

      {postActionActivity && postAction?.kind === 'schedule' && (
        <ScheduleCalendarActivityDialog activity={postActionActivity} onClose={() => setPostAction(null)} />
      )}
      {postActionActivity && postAction?.kind === 'upload' && (
        <UploadLinkCalendarDialog activity={postActionActivity} onClose={() => setPostAction(null)} />
      )}
      {postActionActivity && postAction?.kind === 'swap' && (
        <SwapPageCalendarDialog activity={postActionActivity} onClose={() => setPostAction(null)} pages={pages} />
      )}

      {completeActivity && (
        <CompleteCalendarActivityDialog
          key={completeActivity.id}
          activity={completeActivity}
          open
          onClose={() => setCompleteActivityId(null)}
          pages={pages}
        />
      )}

      <CreateActivityDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        defaultDate={createDefaultDate}
        shootingCategories={shootingCategories}
        pages={pages}
        assignList={assignList}
      />

      {viewActivity && (
        <ActivityDetailDialog
          activity={viewActivity}
          open
          onClose={() => setViewActivityId(null)}
          canDelete={canDelete && hasCalendarDeleteAccess}
          onDelete={() => { setDeleteActivity(viewActivity); setViewActivityId(null); }}
          canEdit={canManage}
          onEdit={() => { setEditActivity(viewActivity); setViewActivityId(null); }}
          canChangeStatus={canChangeStatus(viewActivity)}
          onStatusChange={handleStatusChange}
          onPostAction={canPostAction(viewActivity) ? kind => openPostAction(viewActivity, kind) : null}
        />
      )}

      <EditCalendarActivityDialog
        activity={editActivity}
        open={!!editActivity}
        onClose={() => setEditActivity(null)}
        onSubmit={body => editMutation.mutate({ id: editActivity!.id, body })}
        isPending={editMutation.isPending}
        pages={pages}
        categories={shootingCategories}
      />

      <AlertDialog open={!!cancelActivity} onOpenChange={v => !v && setCancelActivity(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{CANCEL_CONFIRM_TITLE}</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-foreground">{cancelActivity?.title}</span> — {CANCEL_CONFIRM_TEXT}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep activity</AlertDialogCancel>
            <AlertDialogAction
              // This app's AlertDialogAction is a plain button — close the confirm ourselves.
              onClick={() => {
                if (cancelActivity) statusMutation.mutate({ id: cancelActivity.id, status: 'cancelled' });
                setCancelActivity(null);
              }}
              className="bg-destructive text-destructive-foreground"
            >
              Cancel activity
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteActivity} onOpenChange={v => !v && setDeleteActivity(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from calendar?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteActivity?.title}" will be removed from the schedule calendar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteActivity && deleteMutation.mutate(deleteActivity.id)}
              className="bg-destructive text-destructive-foreground"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
